<script setup lang="ts">
import { Check, ChevronDown, UserRoundPlus } from 'lucide-vue-next';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import TaskAssigneeDisplay from '@/components/tasks/TaskAssigneeDisplay.vue';
import type { TaskAssigneeOption } from '~/lib/taskAssignees';

const props = withDefaults(defineProps<{
    modelValue?: string[],
    options?: TaskAssigneeOption[],
    placeholder?: string,
    triggerClass?: string,
    disabled?: boolean,
    showChevron?: boolean,
    minimalTrigger?: boolean,
    avatarOnlyTrigger?: boolean,
}>(), {
    modelValue: () => [],
    options: () => [],
    placeholder: 'Unassigned',
    triggerClass: '',
    disabled: false,
    showChevron: true,
    minimalTrigger: false,
    avatarOnlyTrigger: false,
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void;
}>();

const open = ref(false);

const selectedOptions = computed(() => {
    return props.options.filter(option => props.modelValue.includes(option.id));
});

function initials(label: string) {
    return label
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() || '')
        .join('');
}

function toggle(id: string) {
    const next = new Set(props.modelValue);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emit('update:modelValue', Array.from(next));
}

function clearAll() {
    emit('update:modelValue', []);
}
</script>

<template>
    <Popover v-model:open="open">
        <PopoverTrigger as-child>
            <button
                type="button"
                :disabled="disabled"
                :class="[
                    'inline-flex items-center justify-between gap-3 text-left',
                    avatarOnlyTrigger
                        ? 'min-h-9 min-w-9 justify-center rounded-full bg-transparent p-1 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        : minimalTrigger
                        ? 'w-full rounded-md bg-transparent px-2 py-1.5 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                        : 'w-full rounded-md border border-input bg-background px-3 py-2 shadow-sm transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                    triggerClass,
                ]"
                :title="avatarOnlyTrigger
                    ? (selectedOptions.length === 1 ? selectedOptions[0]?.label : selectedOptions.length ? `${selectedOptions.length} assignees` : placeholder)
                    : undefined"
            >
                <span v-if="avatarOnlyTrigger && selectedOptions.length" class="flex -space-x-2">
                    <Avatar
                        v-for="option in selectedOptions.slice(0, 3)"
                        :key="option.id"
                        class="size-7 border-2 border-background"
                    >
                        <AvatarImage v-if="option.avatarURL" :src="option.avatarURL" :alt="option.label" />
                        <AvatarFallback class="bg-primary/15 text-[9px] font-semibold text-primary">
                            {{ initials(option.label) }}
                        </AvatarFallback>
                    </Avatar>
                    <span
                        v-if="selectedOptions.length > 3"
                        class="inline-flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted text-[9px] font-medium text-muted-foreground"
                    >
                        +{{ selectedOptions.length - 3 }}
                    </span>
                </span>
                <UserRoundPlus v-else-if="avatarOnlyTrigger" class="size-4 text-muted-foreground" />
                <TaskAssigneeDisplay
                    v-else-if="selectedOptions.length"
                    :assignees="selectedOptions"
                    compact
                />
                <span v-else-if="!avatarOnlyTrigger" class="text-sm text-muted-foreground">{{ placeholder }}</span>
                <ChevronDown v-if="showChevron && !avatarOnlyTrigger" class="h-4 w-4 text-muted-foreground" />
            </button>
        </PopoverTrigger>

        <PopoverContent class="w-80 p-3" align="start">
            <div class="space-y-3">
                <div class="flex items-center justify-between gap-2">
                    <div>
                        <p class="text-sm font-medium">Assign teammates</p>
                        <p class="text-xs text-muted-foreground">Select one or more people for this task.</p>
                    </div>
                    <button
                        v-if="modelValue.length > 0"
                        type="button"
                        class="inline-flex h-7 items-center rounded-md px-2 text-xs transition-colors hover:bg-muted/60"
                        @click="clearAll"
                    >
                        Clear
                    </button>
                </div>

                <div class="max-h-72 space-y-1 overflow-y-auto">
                    <button
                        v-for="option in options"
                        :key="option.id"
                        type="button"
                        class="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-muted/50"
                        @click="toggle(option.id)"
                    >
                        <Checkbox :model-value="modelValue.includes(option.id)" />
                        <Avatar class="h-8 w-8">
                            <AvatarImage v-if="option.avatarURL" :src="option.avatarURL" :alt="option.label" />
                            <AvatarFallback class="bg-primary/15 text-[10px] font-semibold text-primary">
                                {{ initials(option.label) }}
                            </AvatarFallback>
                        </Avatar>
                        <div class="min-w-0 flex-1">
                            <p class="truncate text-sm font-medium text-foreground">{{ option.label }}</p>
                        </div>
                        <Check v-if="modelValue.includes(option.id)" class="h-4 w-4 text-primary" />
                    </button>
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>
